import { useIsWeb } from '@/hooks/useIsWeb';
import React, { ChangeEvent } from 'react';
import { Button, XStack } from 'tamagui';
import { StyledInput } from './input.styled';
import { Ionicons } from '@expo/vector-icons';

/** Props for the custom Input component. */
interface InputProps {
  /** The current value of the input */
  value?: string;
  /** If numeric, min value for input value */
  min?: number;
  /** If numeric, max value for input value */
  max?: number;
  /** Callback when the input value changes */
  onChangeText?: (text: string) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Keyboard type for mobile keyboards */
  keyboardType?: 'default' | 'numeric' | 'email-address' | 'phone-pad';
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Whether the input is editable (default: true) */
  editable?: boolean;
  /** Whether the input is secure (password field) */
  secureTextEntry?: boolean;
  /** Maximum length of input */
  maxLength?: number;
  /** Whether to auto-capitalize */
  autoCapitalize?: 'none' | 'sentences' | 'words' | 'characters';
  /** Whether to auto-correct */
  autoCorrect?: boolean;
  /** Flex value for sizing */
  flex?: number;
  /** Width of the input */
  width?: string | number;
  /** Whether to show a clear button (default: false) */
  clearable?: boolean;
}

/**
 * Generic Input component for text input throughout the app.
 *
 * Features:
 * - Consistent styling using Tamagui design tokens
 * - No border on focus (prevents visual distraction)
 * - Supports all standard input props
 * - Optional clear button functionality
 * - Works across web and mobile
 *
 * @example
 * <Input
 *   value={text}
 *   onChangeText={setText}
 *   placeholder="Enter text"
 *   keyboardType="numeric"
 *   clearable
 * />
 */
export const Input = ({
  value,
  min,
  max,
  onChangeText,
  placeholder,
  keyboardType = 'default',
  disabled = false,
  editable = true,
  secureTextEntry = false,
  maxLength,
  autoCapitalize = 'none',
  autoCorrect = false,
  flex,
  width,
  clearable = false,
}: InputProps): React.ReactElement => {
  const isWeb = useIsWeb();

  const shouldShowClearButton =
    clearable && value && value.length > 0 && !disabled;

  const handleValueChange = (value: string): void => {
    // Early return for empty input or negative sign
    if (value === '' || value === '-') {
      onChangeText?.(value);
      return;
    }

    // Only validate numeric values if min/max are provided
    if (min !== undefined || max !== undefined) {
      const numValue = Number(value);

      if (
        isNaN(numValue) || // Check if the input is a valid number
        (min !== undefined && numValue < min) || // Validate minimum value
        (max !== undefined && numValue > max) // Validate maximum value
      ) {
        return;
      }
    }

    onChangeText?.(value);
  };

  const handleNativeTextChange = (text: string): void => {
    handleValueChange(text);
  };

  const handleWebChange = (event: ChangeEvent<HTMLInputElement>): void => {
    handleValueChange(event.target?.value ?? '');
  };

  const handleClear = (): void => {
    onChangeText?.('');
  };

  if (!clearable) {
    return (
      <StyledInput
        value={value}
        min={min}
        max={max}
        onChangeText={isWeb ? undefined : handleNativeTextChange}
        onChange={isWeb ? handleWebChange : undefined}
        placeholder={placeholder}
        keyboardType={keyboardType}
        disabled={disabled}
        editable={editable}
        secureTextEntry={secureTextEntry}
        maxLength={maxLength}
        autoCapitalize={autoCapitalize}
        autoCorrect={autoCorrect}
        flex={flex}
        width={width}
      />
    );
  }

  return (
    <XStack alignItems="center" position="relative" flex={flex} width={width}>
      <StyledInput
        value={value}
        min={min}
        max={max}
        onChangeText={isWeb ? undefined : handleNativeTextChange}
        onChange={isWeb ? handleWebChange : undefined}
        placeholder={placeholder}
        keyboardType={keyboardType}
        disabled={disabled}
        editable={editable}
        secureTextEntry={secureTextEntry}
        maxLength={maxLength}
        autoCapitalize={autoCapitalize}
        autoCorrect={autoCorrect}
        flex={1}
        paddingRight={shouldShowClearButton ? 40 : undefined}
      />
      {shouldShowClearButton && (
        <Button
          position="absolute"
          right="$2"
          size="$2"
          backgroundColor="transparent"
          borderWidth={0}
          padding={0}
          onPress={handleClear}
          icon={<Ionicons name="close" size={24} />}
          chromeless
        />
      )}
    </XStack>
  );
};
